import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { LogoMark } from "./Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  "Quanto costa rifare un tetto?",
  "Il sopralluogo è gratuito?",
  "In quali zone lavorate?",
];

const WELCOME = {
  role: "assistant",
  content: "Ciao! Sono l'assistente virtuale di MERLO coperture. Chiedimi pure informazioni sui nostri servizi.",
};

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);
  const sid = useRef(
    (() => {
      let s = localStorage.getItem("merlo_chat_sid");
      if (!s) {
        s = crypto.randomUUID();
        localStorage.setItem("merlo_chat_sid", s);
      }
      return s;
    })()
  );

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    try {
      const resp = await fetch(`${API}/assistente`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sid.current, message: msg }),
      });
      if (!resp.ok || !resp.body) throw new Error("bad response");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        const current = acc;
        setMessages((m) => {
          const c = [...m];
          c[c.length - 1] = { role: "assistant", content: current };
          return c;
        });
      }
    } catch {
      setMessages((m) => {
        const c = [...m];
        c[c.length - 1] = {
          role: "assistant",
          content: "Mi dispiace, c'è un problema tecnico. Puoi chiamarci al +39 351 747 9343.",
        };
        return c;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <motion.button
        data-testid="chat-widget-button"
        onClick={() => setOpen((o) => !o)}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.8, type: "spring", stiffness: 260, damping: 18 }}
        className="fixed z-[70] bottom-24 md:bottom-6 right-4 md:right-6 w-14 h-14 rounded-full bg-[#D97736] hover:bg-[#E88D52] text-[#0B0C0E] flex items-center justify-center shadow-[0_12px_40px_rgba(217,119,54,0.4)] transition-colors"
        aria-label={open ? "Chiudi assistente" : "Apri assistente virtuale"}
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed z-[70] bottom-40 md:bottom-24 right-4 md:right-6 w-[calc(100vw-2rem)] max-w-sm h-[480px] max-h-[70vh] bg-[#13151A] border border-white/10 rounded-3xl shadow-[0_30px_80px_rgba(0,0,0,0.6)] flex flex-col overflow-hidden"
          >
            <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10 bg-[#0B0C0E]/60">
              <LogoMark size={30} />
              <div>
                <p className="font-syne font-semibold text-sm text-[#F3F4F6]">Assistente MERLO</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#D97736] flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Online
                </p>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div
                  key={i}
                  data-testid={`chat-message-${m.role}-${i}`}
                  className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "ml-auto bg-[#D97736] text-[#0B0C0E] rounded-br-md"
                      : "bg-[#1A1D24] text-[#E5E7EB] rounded-bl-md"
                  }`}
                >
                  {(m.role === "assistant" ? m.content.replace(/\*\*(.*?)\*\*/g, "$1") : m.content) ||
                    (busy && i === messages.length - 1 ? "…" : "")}
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            {messages.length === 1 && (
              <div className="px-4 pb-2 flex flex-wrap gap-2">
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={s}
                    data-testid={`chat-suggestion-${i}`}
                    onClick={() => send(s)}
                    className="text-xs font-outfit border border-[#D97736]/40 text-[#D97736] hover:bg-[#D97736]/10 rounded-full px-3 py-1.5 transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="flex items-center gap-2 px-4 py-3 border-t border-white/10 bg-[#0B0C0E]/60"
            >
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Scrivi la tua domanda..."
                maxLength={500}
                className="flex-1 bg-[#0B0C0E] border border-white/10 focus:border-[#D97736]/60 outline-none rounded-full px-4 py-2.5 text-sm text-[#F3F4F6] placeholder:text-[#6B7280] transition-colors"
              />
              <button
                type="submit"
                data-testid="chat-send-button"
                disabled={busy || !input.trim()}
                className="w-10 h-10 shrink-0 rounded-full bg-[#D97736] hover:bg-[#E88D52] disabled:opacity-50 text-[#0B0C0E] flex items-center justify-center transition-colors"
                aria-label="Invia messaggio"
              >
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
